import { useLocation } from 'react-router-dom';
import { detectAppFromUrl } from '@/config/app-registry';

export interface AIUsageContext {
  appName: string;
  functionName?: string;
  customContext?: Record<string, any>;
}

/**
 * Hook to automatically detect the current app context for AI usage tracking
 * This ensures proper labeling in usage analytics without manual context passing
 */
export const useContextAwareAIUsage = () => {
  const location = useLocation();

  /**
   * Get the current app context based on the URL
   */
  const getCurrentAppContext = (): string => {
    return detectAppFromUrl(location.pathname);
  };

  /**
   * Create AI usage context with automatic app detection
   */
  const createUsageContext = (
    functionName?: string,
    customAppName?: string,
    customContext?: Record<string, any>
  ): AIUsageContext => {
    return {
      appName: customAppName || getCurrentAppContext(),
      functionName,
      customContext,
    };
  };

  /**
   * Create context specifically for image generation with automatic detection
   */
  const createImageGenerationContext = (
    functionName: string = 'Image Generation',
    customAppName?: string
  ): AIUsageContext => {
    const detectedApp = getCurrentAppContext();
    
    // Override function name based on detected app for more specific tracking
    let specificFunctionName = functionName;
    if (detectedApp === 'Content Generator' && functionName === 'Image Generation') {
      specificFunctionName = 'Workbook Image Generation';
    } else if (detectedApp === 'Account' && functionName === 'Image Generation') {
      specificFunctionName = 'Avatar Generation';
    }

    return createUsageContext(specificFunctionName, customAppName);
  };

  /**
   * Create context for text generation
   */
  const createTextGenerationContext = (
    functionName: string = 'Text Generation',
    customAppName?: string
  ): AIUsageContext => {
    return createUsageContext(functionName, customAppName);
  };

  /**
   * Create context for research generation
   */
  const createResearchContext = (
    functionName: string = 'Research Generation', 
    customAppName?: string
  ): AIUsageContext => {
    return createUsageContext(functionName, customAppName);
  };

  /**
   * Create context for AI agent interactions
   */
  const createAgentContext = (
    functionName: string = 'Chat Conversation',
    customAppName?: string
  ): AIUsageContext => {
    return createUsageContext(functionName, customAppName);
  };

  return {
    getCurrentAppContext,
    createUsageContext,
    createImageGenerationContext,
    createTextGenerationContext, 
    createResearchContext,
    createAgentContext,
  };
};

export default useContextAwareAIUsage; 